import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ProfaseModal from '../../commons/ProfaseModal';
import Confirm, { useConfirm } from '../../commons/Confirm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { profaseDataGridSx } from '../../commons/profaseDataGridTheme';
import { useAppDispatch, useAppSelector } from '../../store';
import { getEntity } from '../../manager/workflow-def.reducer';
import {
  fetchInstanciasForWorkflowStart,
  type ProfasePeriodoActividadInstanciaRef,
} from '../../shared/calendario-workflow';
import { formatTipoPeriodoLabel, formatWorkflowPeriodicidadLabel } from '../../shared/workflow-periodicidad';
import { anularProcesosWorkflowUrl, generarProcesosWorkflowUrl, sortPeriodoInstanciasNewestFirst } from './procesos-workflow-routes';
import { filterInstanciasByPeriodoId } from './procesos-no-creados';

interface GenerarProcesosResponse {
  cantidad?: number;
  cantidadOmitidas?: number;
}

interface AnularProcesosResponse {
  cantidad?: number;
  cantidadOmitidas?: number;
}

function axiosErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail ?? data?.message ?? fallback;
  }
  return fallback;
}

function periodoSelectLabel(row: ProfasePeriodoActividadInstanciaRef): string {
  const act = row.periodoActividad?.nombre?.trim();
  const per = row.periodo?.descripcion?.trim();
  if (act && per) return `${act} · ${per}`;
  return per ?? act ?? `#${row.id}`;
}

function workflowTitle(workflow: { id?: number; nombre?: string } | null | undefined): string {
  const nombre = workflow?.nombre?.trim();
  if (nombre) return nombre;
  return workflow?.id != null ? `Workflow #${workflow.id}` : 'Workflow';
}

export interface ProcesosWorkflowPeriodosViewProps {
  workflowId: number;
  empresaId?: number;
  empresaLabel?: string;
  periodoId?: number | null;
  active?: boolean;
  onProcesosCreated?: () => void;
}

export function ProcesosWorkflowPeriodosView({
  workflowId,
  empresaId,
  empresaLabel,
  periodoId,
  active = true,
  onProcesosCreated,
}: ProcesosWorkflowPeriodosViewProps) {
  const dispatch = useAppDispatch();
  const workflow = useAppSelector(s => s.workflowDef.entity) as {
    id?: number;
    nombre?: string;
    periodicidad?: string | null;
    workflowClase?: { nombre?: string } | null;
  };
  const workflowLoading = useAppSelector(s => s.workflowDef.loading);
  const workflowError = useAppSelector(s => s.workflowDef.errorMessage);
  const showingWorkflow = Number(workflow?.id) === workflowId;
  const infoPending = workflowLoading || (!workflowError && !showingWorkflow);

  const [instancias, setInstancias] = useState<ProfasePeriodoActividadInstanciaRef[]>([]);
  const [assignedCatalogCount, setAssignedCatalogCount] = useState(0);
  const [periodosLoading, setPeriodosLoading] = useState(false);
  const [actionInstanciaId, setActionInstanciaId] = useState<number | null>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const loadPeriodos = useCallback(async () => {
    if (!active || !workflowId) return;
    setPeriodosLoading(true);
    try {
      const result = await fetchInstanciasForWorkflowStart(workflowId);
      setInstancias(sortPeriodoInstanciasNewestFirst(result.instancias));
      setAssignedCatalogCount(result.assignedCatalogIds.length);
    } catch {
      setInstancias([]);
      setAssignedCatalogCount(0);
      showToast('error', 'No se pudieron cargar los periodos generados.');
    } finally {
      setPeriodosLoading(false);
    }
  }, [active, workflowId]);

  useEffect(() => {
    if (!active || !workflowId) return;
    dispatch(getEntity(workflowId));
  }, [active, dispatch, workflowId]);

  useEffect(() => {
    void loadPeriodos();
  }, [loadPeriodos]);

  const runGenerarProcesos = useCallback(async (instancia: ProfasePeriodoActividadInstanciaRef) => {
    setActionInstanciaId(instancia.id);
    setConfirmLoading(true);
    try {
      const res = await axios.post<GenerarProcesosResponse>(generarProcesosWorkflowUrl(workflowId), {
        periodoActividadInstanciaId: instancia.id,
        ...(empresaId != null ? { empresaId } : {}),
      });
      const creados = Number(res.data?.cantidad) || 0;
      const omitidas = Number(res.data?.cantidadOmitidas) || 0;
      const omitidasMsg = omitidas > 0 ? ` (${omitidas} omitidos)` : '';
      showToast(
        'success',
        `Se crearon ${creados} proceso${creados === 1 ? '' : 's'} de "${workflowTitle(workflow)}" para "${periodoSelectLabel(instancia)}"${empresaId != null ? ` · ${empresaLabel?.trim() || `empresa #${empresaId}`}` : ''}${omitidasMsg}.`,
      );
      onProcesosCreated?.();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudieron crear los procesos.'));
    } finally {
      setActionInstanciaId(null);
      setConfirmLoading(false);
      dismiss();
    }
  }, [dismiss, empresaId, empresaLabel, onProcesosCreated, setConfirmLoading, showToast, workflow, workflowId]);

  const handleCrearProcesosClick = useCallback((instancia: ProfasePeriodoActividadInstanciaRef) => {
    ask(
      <>
        ¿Crear procesos de <strong>{workflowTitle(workflow)}</strong> para{' '}
        <strong>{periodoSelectLabel(instancia)}</strong>
        {empresaId != null ? <> en <strong>{empresaLabel?.trim() || `empresa #${empresaId}`}</strong></> : null}
        ?
        {' '}
        {empresaId != null
          ? 'Se generará la instancia de esta empresa (omite duplicados).'
          : 'Se generará una instancia por cada empresa del workflow (omite duplicados).'}
      </>,
      () => { void runGenerarProcesos(instancia); },
      { title: 'Crear procesos', confirmLabel: 'Crear procesos', variant: 'info' },
    );
  }, [ask, empresaId, empresaLabel, runGenerarProcesos, workflow]);

  const runAnularProcesos = useCallback(async (instancia: ProfasePeriodoActividadInstanciaRef) => {
    setActionInstanciaId(instancia.id);
    setConfirmLoading(true);
    try {
      const res = await axios.post<AnularProcesosResponse>(anularProcesosWorkflowUrl(workflowId), {
        periodoActividadInstanciaId: instancia.id,
        ...(empresaId != null ? { empresaId } : {}),
      });
      const eliminados = Number(res.data?.cantidad) || 0;
      const conservados = Number(res.data?.cantidadOmitidas) || 0;
      const conservadosMsg = conservados > 0 ? ` (${conservados} finalizados conservados)` : '';
      showToast(
        'success',
        `Se anularon ${eliminados} proceso${eliminados === 1 ? '' : 's'} de "${workflowTitle(workflow)}" para "${periodoSelectLabel(instancia)}"${empresaId != null ? ` · ${empresaLabel?.trim() || `empresa #${empresaId}`}` : ''}${conservadosMsg}.`,
      );
      onProcesosCreated?.();
    } catch (err) {
      showToast('error', axiosErrorMessage(err, 'No se pudieron anular los procesos.'));
    } finally {
      setActionInstanciaId(null);
      setConfirmLoading(false);
      dismiss();
    }
  }, [dismiss, empresaId, empresaLabel, onProcesosCreated, setConfirmLoading, showToast, workflow, workflowId]);

  const handleAnularProcesosClick = useCallback((instancia: ProfasePeriodoActividadInstanciaRef) => {
    ask(
      <>
        ¿Anular los procesos no finalizados de <strong>{workflowTitle(workflow)}</strong> para{' '}
        <strong>{periodoSelectLabel(instancia)}</strong>
        {empresaId != null ? <> en <strong>{empresaLabel?.trim() || `empresa #${empresaId}`}</strong></> : null}
        ?
        {' '}
        {empresaId != null
          ? 'Se elimina el proceso de esta empresa si no terminó; el finalizado se conserva.'
          : 'Se eliminan los de este workflow que no terminaron; los finalizados se conservan.'}
      </>,
      () => { void runAnularProcesos(instancia); },
      { title: 'Anular procesos', confirmLabel: 'Anular', variant: 'danger' },
    );
  }, [ask, empresaId, empresaLabel, runAnularProcesos, workflow]);

  const cols: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', flex: 0.4, minWidth: 56 },
      {
        field: 'periodoActividad.nombre',
        headerName: 'Catálogo',
        flex: 1,
        minWidth: 130,
        renderCell: params => {
          const nombre = (params.row as ProfasePeriodoActividadInstanciaRef).periodoActividad?.nombre;
          return nombre ?? <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
      {
        field: 'periodo.descripcion',
        headerName: 'Periodo',
        flex: 1,
        minWidth: 140,
        renderCell: params => {
          const label = (params.row as ProfasePeriodoActividadInstanciaRef).periodo?.descripcion?.trim();
          return label || <span style={{ color: 'var(--crm-text-muted)' }}>—</span>;
        },
      },
      {
        field: 'periodo.tipo',
        headerName: 'Tipo',
        flex: 0.5,
        minWidth: 88,
        renderCell: params => {
          const row = params.row as ProfasePeriodoActividadInstanciaRef;
          return formatTipoPeriodoLabel(row.periodo?.tipo ?? row.periodoActividad?.tipoPeriodo);
        },
      },
      {
        field: '__actions',
        headerName: '',
        flex: 1.2,
        minWidth: 260,
        sortable: false,
        renderCell: params => {
          const row = params.row as ProfasePeriodoActividadInstanciaRef;
          const busy = actionInstanciaId === row.id;
          return (
            <div className="procesos-periodos-actions-cell">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={busy}
                onClick={() => handleCrearProcesosClick(row)}
              >
                Crear procesos
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                disabled={busy}
                onClick={() => handleAnularProcesosClick(row)}
              >
                Anular
              </button>
            </div>
          );
        },
      },
    ],
    [actionInstanciaId, handleAnularProcesosClick, handleCrearProcesosClick],
  );

  const visibleInstancias = useMemo(
    () => filterInstanciasByPeriodoId(instancias, periodoId),
    [instancias, periodoId],
  );

  const emptyHint = assignedCatalogCount === 0
    ? 'No hay periodos actividad asignados a este workflow.'
    : periodoId != null
      ? 'No hay instancias de periodo generadas para ese periodo.'
      : 'No hay instancias de periodo generadas para los catálogos asignados al workflow.';

  return (
    <>
      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <div style={s.info}>
        <div style={s.infoItem}>
          <span style={s.infoLabel}>Workflow</span>
          <span style={s.infoValue}>{infoPending ? '…' : workflowTitle(workflow)}</span>
        </div>
        {empresaId != null ? (
          <div style={s.infoItem}>
            <span style={s.infoLabel}>Empresa</span>
            <span style={s.infoValue}>{empresaLabel?.trim() || `Empresa #${empresaId}`}</span>
          </div>
        ) : null}
        <div style={s.infoItem}>
          <span style={s.infoLabel}>Periodicidad</span>
          <span style={s.infoValue}>
            {infoPending ? '…' : formatWorkflowPeriodicidadLabel(workflow?.periodicidad)}
          </span>
        </div>
        <div style={s.infoItem}>
          <span style={s.infoLabel}>Clase</span>
          <span style={s.infoValue}>
            {infoPending ? '…' : (workflow?.workflowClase?.nombre?.trim() || '—')}
          </span>
        </div>
      </div>

      {workflowError ? (
        <p style={s.error}>{workflowError}</p>
      ) : null}

      <h3 style={s.sectionTitle}>Periodos generados</h3>
      <p style={s.hint}>
        {empresaId != null
          ? 'Crear procesos genera la instancia de esta empresa para el periodo seleccionado. Anular solo afecta a esa empresa.'
          : 'Crear procesos genera una instancia de este workflow por empresa, solo para el periodo seleccionado. Anular elimina los no finalizados de este workflow; no toca los de otros workflows.'}
      </p>

      <DataGrid
        rows={visibleInstancias}
        columns={cols}
        getRowId={row => row.id}
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        loading={periodosLoading}
        localeText={{ noRowsLabel: emptyHint }}
        sx={{
          ...profaseDataGridSx,
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
          '& .procesos-periodos-actions-cell': {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
          },
        }}
      />
    </>
  );
}

interface ProcesosWorkflowPeriodosModalProps {
  open: boolean;
  workflowId: number;
  empresaId?: number;
  empresaLabel?: string;
  periodoId?: number | null;
  backLabel?: string;
  onClose: () => void;
  onBack: () => void;
  onProcesosCreated?: () => void;
}

export default function ProcesosWorkflowPeriodosModal({
  open,
  workflowId,
  empresaId,
  empresaLabel,
  periodoId,
  backLabel = 'Workflows',
  onClose,
  onBack,
  onProcesosCreated,
}: ProcesosWorkflowPeriodosModalProps) {
  return (
    <ProfaseModal
      open={open}
      titleId="procesos-workflow-periodos-modal-title"
      size="xlarge"
      scroll
    >
      <div style={s.header}>
        <h2 id="procesos-workflow-periodos-modal-title" className="profase-modal-title m-0">
          <FaProjectDiagram style={{ marginRight: 8, verticalAlign: '-2px' }} />
          {empresaId != null ? 'Periodos' : 'Workflow'}
        </h2>
        <div style={s.headerActions}>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onBack}>
            {backLabel}
          </button>
          <button type="button" className="profase-form-btn-secondary btn-sm" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
      <ProcesosWorkflowPeriodosView
        workflowId={workflowId}
        empresaId={empresaId}
        empresaLabel={empresaLabel}
        periodoId={periodoId}
        active={open}
        onProcesosCreated={onProcesosCreated}
      />
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  info: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    margin: '8px 0 16px',
    padding: '12px 14px',
    borderRadius: 10,
    background: 'var(--crm-surface-muted, #f8fafc)',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 140,
  },
  infoLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--crm-text-secondary)',
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--crm-body-text, #0f172a)',
  },
  sectionTitle: {
    margin: '0 0 4px',
    fontSize: 15,
    fontWeight: 700,
  },
  hint: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  error: {
    margin: '0 0 12px',
    fontSize: 13,
    color: '#b91c1c',
  },
};
